import React, { useEffect, useMemo, useState } from "react";

export default function OpsActionModal({
  open,
  title,
  hint,
  reasons = [],
  confirmLabel = "Submit",
  onClose,
  onConfirm,
  children,
}) {
  const [reasonCode, setReasonCode] = useState(reasons?.[0]?.code || "SOP_REQUIRED");
  const [reasonText, setReasonText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReasonCode(reasons?.[0]?.code || "SOP_REQUIRED");
    setReasonText("");
  }, [open, title, reasons]);

  const ok = useMemo(() => String(reasonCode || "").trim().length > 0, [reasonCode]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-end md:items-center justify-center p-3">
      <div className="w-full max-w-xl rounded-xl bg-white border border-slate-200 shadow-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200">
          <div className="text-xs tracking-wider text-slate-500">Confirm action</div>
          <div className="mt-1 text-xl tracking-tight text-slate-900">{title}</div>
          <div className="mt-1 text-sm text-slate-600">{hint || "This will be saved in audit logs."}</div>
        </div>

        <div className="px-5 py-4 space-y-3">
          {children ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">{children}</div>
          ) : null}

          <div>
            <div className="text-sm text-slate-700">Reason *</div>
            <select
              className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm bg-white"
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value)}
            >
              {(reasons?.length ? reasons : [{ code: "SOP_REQUIRED", label: "SOP required action" }]).map((r) => (
                <option key={r.code} value={r.code}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div className="text-sm text-slate-700">Notes (optional)</div>
            <textarea
              className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm min-h-[90px]"
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              placeholder="Write short explanation…"
            />
          </div>
        </div>

        <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button
            className="h-9 rounded-md border border-slate-200 px-4 text-sm font-medium hover:bg-slate-50"
            disabled={busy}
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
          <button
            className="h-9 rounded-md bg-slate-900 px-4 text-sm font-medium text-white disabled:opacity-50"
            disabled={!ok || busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onConfirm({ reasonCode, reasonText });
                onClose?.();
              } finally {
                setBusy(false);
              }
            }}
            type="button"
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
